import * as ParseColor from 'parse-color';

import '../../less/mapp.mvc-studio.variables.branding.css';
import { MetadataClient } from 'mappClient/metadataClient';

const prefix = '--hxg-';
const contrastThreshold = 160;
type storageKey = 'primaryColor' | 'secondaryColor' | 'isDefault';

export abstract class Branding {

    static updateBranding = (primary: string, secondary: string, isDefault: boolean) => {

        Branding.setLocalStorage('primaryColor', primary);
        Branding.setLocalStorage('secondaryColor', secondary);
        Branding.setLocalStorage('isDefault', isDefault.toString());
        Branding.setRootVariable('primary', primary);
        Branding.setRootVariable('secondary', secondary);
    }

    static getLocalStorage = (name: storageKey) => {
        let lsKey = MetadataClient.storageId;
        return localStorage.getItem(lsKey) ? JSON.parse(localStorage.getItem(lsKey))[name] : undefined;
    }

    static setLocalStorage = (name: storageKey, value: string) => {
        let lsKey = MetadataClient.storageId;
        let storage = JSON.parse(localStorage.getItem(lsKey));
        storage[name] = value;
        localStorage.setItem(lsKey, JSON.stringify(storage));
    }

    static getRootVariable = (name: 'primary' | 'secondary') => {

        let getValue = (k: string) => getComputedStyle(document.documentElement).getPropertyValue(k);
        let key = `${prefix}${name}`;

        let hsl = {
            h: getValue(`${key}-h`),
            s: getValue(`${key}-s`),
            l: getValue(`${key}-l`)
        };

        let parsed = ParseColor(`hsl(${hsl.h}, ${hsl.s}, ${hsl.l})`);

        return parsed.hex;
    }

    static setRootVariable = (name: 'primary' | 'secondary', value: string) => {
        let setValue = (k, v) => document.documentElement.style.setProperty(k, v);
        let parsed = ParseColor(value);
        let key = `${prefix}${name}`;

        if (parsed.hsl) {
            setValue(`${key}-h`, parsed.hsl[0].toString());
            setValue(`${key}-s`, parsed.hsl[1] + '%');
            setValue(`${key}-l`, parsed.hsl[2] + '%');
        }
    }

    static setRootHighContrastVariables = () => {
        let setValue = (k: string, v: string) => document.documentElement.style.setProperty(k, v);

        let primary: Array<number> = ParseColor(Branding.getRootVariable('primary')).rgb;
        let secondary: Array<number> = ParseColor(Branding.getRootVariable('secondary')).rgb;
        let highContrast: { main: string, bland: string };

        if (primary) {
            highContrast = Branding.getHighContrastColors(primary[0], primary[1], primary[2]);
            setValue(`--hxg-primary-highcontrast`, highContrast.main);
            setValue(`--hxg-primary-highcontrast-bland`, highContrast.bland);
        }

        if (secondary) {
            highContrast = Branding.getHighContrastColors(secondary[0], secondary[1], secondary[2]);
            setValue(`--hxg-secondary-highcontrast`, highContrast.main);
            setValue(`--hxg-secondary-highcontrast-bland`, highContrast.bland);
        }
    }

    static getHighContrastColors = (r: number, g: number, b: number): { main: string, bland: string } => {

        let brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;

        return brightness > contrastThreshold
            ? { main: '#000000', bland: '#505050' }
            : { main: '#ffffff', bland: '#e4e4e4' };
    }

    static isDark = (r: number, g: number, b: number): boolean => {
        return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.3;
    }

    static isValidColor = (input: string) => {
        let color = ParseColor(input.padEnd(7));
        return color.hsl !== undefined;
    }

    static setBrandingColors = () => {
        Branding.setRootVariable('primary', Branding.getLocalStorage('primaryColor'));
        Branding.setRootVariable('secondary', Branding.getLocalStorage('secondaryColor'));
        Branding.setRootHighContrastVariables();
    }
}